import React, { memo } from 'react';
import { Badge } from 'antd';
import { TagOutlined } from '@ant-design/icons';
import AvatarStatus from 'components/shared-components/AvatarStatus';
import moment from 'moment';
import ChatBadges from './ChatBadges';

const areEqual = (prevProps, nextProps) => {
  const prevItemId = prevProps.item?.id ?? `fallback-id-${prevProps.index}`;
  const nextItemId = nextProps.item?.id ?? `fallback-id-${nextProps.index}`;

  // Always re-render if selection state or tag visibility changes
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.showTag[prevItemId] !== nextProps.showTag[nextItemId]) return false;

  // Deep-compare the fields that actually affect the rendered output
  const prev = prevProps.item;
  const next = nextProps.item;
  if (prev === next) return true; // Same reference, nothing changed

  return (
    prev?.unread === next?.unread &&
    prev?.name === next?.name &&
    prev?.lastMessage?.id === next?.lastMessage?.id &&
    prev?.lastMessage?.text === next?.lastMessage?.text &&
    prev?.lastMessage?.status === next?.lastMessage?.status &&
    (prev?.lastMessage?.time || prev?.lastMessage?.created_at) ===
      (next?.lastMessage?.time || next?.lastMessage?.created_at)
  );
};

const ChatMenuItem = memo(({
  item,
  index,
  isLast,
  isSelected,
  openChat,
  getSubTitle,
  showTag,
  toggleTagVisibility,
  allBadges,
  setChatList,
  fetchAllBadges
}) => {
  const itemId = item?.id ?? `fallback-id-${index}`;
  const isOldchat = Boolean(item?.is_expired);
  const previewText = getSubTitle(item);

  return (
    <li
      style={{
        backgroundColor: isOldchat ? 'rgb(229 229 229)' : 'transparent',
        listStyle: 'none',
        marginBottom: '2px',
        cursor: 'pointer'
      }}
      className="d-flex flex-column justify-content-start chat-menu-list-item chat-menu-list-item-hover"
      onClick={() => openChat(item?.number)}
    >
      <div className={`d-flex flex-column ${isLast ? 'last' : ''} ${isSelected ? 'selected' : ''}`}>
        <div className="d-flex align-items-center gap-2">
          <div className="w-100 d-flex">
            <AvatarStatus
              text={item?.name}
              name={item?.name}
              hasStatus={item?.lastMessage?.from === 'me'}
              status={item?.lastMessage?.status}
              subTitle={
                <div className="d-flex flex-column align-items-start">
                  <div className="chat-menu-preview-text" title={previewText}>
                    {previewText}
                  </div>
                </div>
              }
            />
            <div className="text-right">
              <div className="chat-menu-list-item-time">
                {moment(item?.lastMessage.time).format('MMM DD, YYYY hh:mm:ss A')}
              </div>
              <div className="d-flex align-items-center gap-2 justify-content-end">
                {item?.unread > 0 && (
                  <div className="d-flex align-items-baseline">
                    <Badge color="green" count={item?.unread} />
                  </div>
                )}
                <div className="d-flex align-items-center">
                  <TagOutlined
                    style={{ fontSize: '12px', cursor: 'pointer' }}
                    className="d-flex"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTagVisibility(itemId);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* We keep ChatBadges conditionally rendered based on showTag to keep it snappy */}
      {showTag[itemId] && (
        <div style={{ padding: '0 10px 10px' }}>
          <ChatBadges
            itemId={itemId}
            report={item}
            setList={setChatList}
            showTag={showTag}
            badges={item?.badges}
            allBadges={allBadges}
            onAssignBadge={(updatedBadges) => fetchAllBadges(false, itemId, updatedBadges)}
          />
        </div>
      )}
    </li>
  );
}, areEqual);

export default ChatMenuItem;
